import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from '@/lib/firebase';
import { api } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { Loader2, Eye, EyeOff, Check, X } from 'lucide-react';
import clsx from 'clsx';

export default function RegisterPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { login } = useAuthStore();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [googleError, setGoogleError] = useState('');
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        password: '',
        confirmPassword: '',
        referralCode: '',
    });
    const [searchParams] = useSearchParams();

    // Pre-fill referral code from URL
    useEffect(() => {
        const ref = searchParams.get('ref');
        if (ref && !formData.referralCode) {
            setFormData(prev => ({ ...prev, referralCode: ref }));
        }
    }, [searchParams]);

    const registerMutation = useMutation({
        mutationFn: () => api.register(formData.email, formData.password, formData.fullName, formData.referralCode),
        onSuccess: (data) => {
            // Redirect to check email page
            navigate('/check-email', { state: { email: data.email } });
        },
    });

    const googleMutation = useMutation({
        mutationFn: ({ idToken, referralCode }: { idToken: string; referralCode?: string }) =>
            api.loginWithGoogle(idToken, referralCode),
        onSuccess: (data) => {
            login(data.user, data.workspace, data.access_token);
            navigate('/dashboard');
        },
        onError: (error) => {
            setGoogleError(error.message || 'Google sign-up failed');
        },
    });

    const handleGoogleSignUp = async () => {
        setGoogleError('');
        setIsGoogleLoading(true);
        try {
            const result = await signInWithPopup(auth, googleProvider);
            const idToken = await result.user.getIdToken();
            // Get referral code from URL query param or form
            const refCode = searchParams.get('ref') || formData.referralCode || undefined;
            googleMutation.mutate({ idToken, referralCode: refCode });
        } catch (error: any) {
            if (error.code === 'auth/popup-closed-by-user') {
                // User closed popup, don't show error
                setIsGoogleLoading(false);
                return;
            }
            setGoogleError(error.message || 'Google sign-up failed');
        } finally {
            setIsGoogleLoading(false);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!isPasswordValid || !passwordsMatch) return;
        registerMutation.mutate();
    };

    // Password validation checks
    const passwordChecks = [
        {
            label: t('auth.password8Chars', 'At least 8 characters'),
            valid: formData.password.length >= 8
        },
        {
            label: t('auth.passwordUppercase', 'One uppercase letter (A-Z)'),
            valid: /[A-Z]/.test(formData.password)
        },
        {
            label: t('auth.passwordLowercase', 'One lowercase letter (a-z)'),
            valid: /[a-z]/.test(formData.password)
        },
        {
            label: t('auth.passwordNumber', 'One number (0-9)'),
            valid: /[0-9]/.test(formData.password)
        },
        {
            label: t('auth.passwordSpecial', 'One special character (!@#$%^&*)'),
            valid: /[^a-zA-Z0-9]/.test(formData.password)
        },
    ];

    const isPasswordValid = passwordChecks.every(check => check.valid);
    const passwordsMatch = formData.password === formData.confirmPassword && formData.confirmPassword.length > 0;

    const isLoading = isGoogleLoading || googleMutation.isPending;

    return (
        <div className="animate-fade-in">
            <h2 className="text-2xl font-bold mb-2">{t('auth.registerTitle')}</h2>
            <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                {t('auth.registerSubtitle')}
            </p>

            {/* 🎉 PROMOTIONAL BANNER */}
            <div className="mb-6 p-4 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl text-white shadow-lg">
                <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                        <span className="text-2xl">🎉</span>
                    </div>
                    <div className="flex-1">
                        <p className="font-bold text-lg mb-1">
                            KHUYẾN MÃI ĐẶC BIỆT!
                        </p>
                        <p className="text-sm text-white/95 leading-relaxed">
                            Nhận <span className="font-bold">MIỄN PHÍ gói PRO</span> trong <span className="font-bold">1 NĂM</span> khi đăng ký ngay!
                        </p>
                        <p className="text-xs text-white/80 mt-2">
                            ⏰ Ưu đãi có hạn đến hết 31/12/2026
                        </p>
                    </div>
                </div>
            </div>

            {/* Google Sign-Up Button */}
            <button
                onClick={handleGoogleSignUp}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path
                            fill="#4285F4"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                            fill="#34A853"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                            fill="#FBBC05"
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                            fill="#EA4335"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                    </svg>
                )}
                <span className="font-medium">
                    {isLoading ? t('common.loading') : t('auth.loginWithGoogle')}
                </span>
            </button>

            {googleError && (
                <div className="p-3 mb-4 bg-danger-50 dark:bg-danger-600/20 text-danger-600 text-sm rounded-lg">
                    {googleError}
                </div>
            )}

            <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-neutral-300 dark:border-neutral-600"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white dark:bg-neutral-800 text-neutral-500">
                        {t('auth.orRegisterEmail')}
                    </span>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label htmlFor="fullName" className="label">{t('auth.fullName')}</label>
                    <input
                        id="fullName"
                        type="text"
                        value={formData.fullName}
                        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                        className="input"
                        placeholder="John Doe"
                        required
                    />
                </div>

                <div>
                    <label htmlFor="email" className="label">{t('auth.email')}</label>
                    <input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="input"
                        placeholder="you@example.com"
                        required
                    />
                </div>

                <div>
                    <label htmlFor="password" className="label">{t('auth.password')}</label>
                    <div className="relative">
                        <input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            className="input pr-10"
                            placeholder="••••••••"
                            required
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                        >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>

                    {/* Password Requirements */}
                    {formData.password && (
                        <div className="mt-3 p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg space-y-1.5">
                            <p className="text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-2">
                                {t('auth.passwordRequirements', 'Password must contain:')}
                            </p>
                            {passwordChecks.map((check, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs">
                                    {check.valid ? (
                                        <Check className="w-3.5 h-3.5 text-success-500" />
                                    ) : (
                                        <X className="w-3.5 h-3.5 text-danger-500" />
                                    )}
                                    <span className={clsx(
                                        check.valid
                                            ? 'text-success-600 dark:text-success-400'
                                            : 'text-danger-600 dark:text-danger-400'
                                    )}>
                                        {check.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Confirm Password */}
                <div>
                    <label htmlFor="confirmPassword" className="label">
                        {t('auth.confirmPassword', 'Confirm Password')}
                    </label>
                    <div className="relative">
                        <input
                            id="confirmPassword"
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={formData.confirmPassword}
                            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                            className={clsx(
                                "input pr-10",
                                formData.confirmPassword && (
                                    passwordsMatch
                                        ? "border-success-500 focus:ring-success-500"
                                        : "border-danger-500 focus:ring-danger-500"
                                )
                            )}
                            placeholder="••••••••"
                            required
                        />
                        <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                        >
                            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>
                    {formData.confirmPassword && !passwordsMatch && (
                        <p className="mt-1 text-xs text-danger-600 flex items-center gap-1">
                            <X className="w-3 h-3" />
                            {t('auth.passwordsDoNotMatch', 'Passwords do not match')}
                        </p>
                    )}
                    {formData.confirmPassword && passwordsMatch && (
                        <p className="mt-1 text-xs text-success-600 flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            {t('auth.passwordsMatch', 'Passwords match')}
                        </p>
                    )}
                </div>

                {/* Referral Code */}
                <div>
                    <label htmlFor="referralCode" className="label">
                        {t('auth.referralCode', 'Mã giới thiệu')} <span className="text-neutral-400 text-xs">({t('common.optional', 'không bắt buộc')})</span>
                    </label>
                    <input
                        id="referralCode"
                        type="text"
                        value={formData.referralCode}
                        onChange={(e) => setFormData({ ...formData, referralCode: e.target.value.toLowerCase() })}
                        className="input"
                        placeholder="abc12345"
                    />
                    <p className="mt-1 text-xs text-neutral-400">
                        {t('auth.referralCodeHint', 'Nhập mã giới thiệu để được ưu đãi')}
                    </p>
                </div>

                {registerMutation.isError && (
                    <div className="p-3 bg-danger-50 dark:bg-danger-600/20 text-danger-600 text-sm rounded-lg">
                        {registerMutation.error?.message || 'Registration failed. Please try again.'}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={registerMutation.isPending || !isPasswordValid || !passwordsMatch}
                    className="btn-primary w-full"
                >
                    {registerMutation.isPending ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {t('common.loading')}
                        </>
                    ) : (
                        t('auth.register')
                    )}
                </button>
            </form>

            <p className="mt-6 text-center text-sm text-neutral-600 dark:text-neutral-400">
                {t('auth.hasAccount')}{' '}
                <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
                    {t('auth.login')}
                </Link>
            </p>
        </div>
    );
}

